<template>
    <div class="grid-content" v-fs-block>
        <div class="grid-item grid-item-full">
            <div v-if="!invoice.items">No invoice items.</div>
            <table class="table" v-if="invoice.items">
                <tbody>
                <tr>
                    <th class="width-half">Description</th>
                    <th class="text-right">Quantity</th>
                    <th class="text-right">Unit Price</th>
                    <th class="text-right">Price</th>
                </tr>
                <tr v-for="invoiceItem in invoice.items">
                    <template>
                        <td class="width-half">
                            <p>{{invoiceItem.description}}</p>
                            <p v-if="invoiceItem.hasPeriod"
                               class="text-muted">
                                {{invoiceItem.periodStartTime | tzDate(invoice.organization.invoiceTimeZone)}} -
                                {{invoiceItem.periodEndTime | tzDate(invoice.organization.invoiceTimeZone)}}</p>
                        </td>
                        <td class="text-right is-numeric">{{invoiceItem.quantity}}</td>
                        <td class="text-right is-numeric" v-if="invoiceItem.isCredit">
                            ({{invoiceItem.unitPrice | monetizeExplicit(invoice.currency)}})
                        </td>
                        <td class="text-right is-numeric" v-else>
                            {{invoiceItem.unitPrice | monetizeExplicit(invoice.currency)}}
                        </td>
                        <td class="text-right is-numeric" v-if="invoiceItem.isCredit">
                            ({{invoiceItem.price | monetizeExplicit(invoice.currency)}})
                        </td>
                        <td class="text-right is-numeric" v-else>
                            {{invoiceItem.price | monetizeExplicit(invoice.currency)}}
                        </td>
                    </template>
                </tr>
                </tbody>
            </table>
            <div class="divider no-margin-top"></div>
            <div class="grid-content">
                <div class="grid-item grid-item-half">
                    <div class="inset-s invoice-notes-autopay">
                        <div class="stack-m invoice-notes">
                            <strong>Notes</strong>
                            <v-inline-edit
                                :disabled="invoice.isNotAllowedToUpdate"
                                v-model="invoice.notes"
                                multiline
                                @input="editInvoice('notes', $event)"/>
                        </div>
                        <div class="invoice-autopay" v-if="invoice.isTangible">
                            <strong>Autopay Schedule</strong>
                            <p>
                                <a href="#" @click="showAutopayModal = true" v-if="hasAutopayScheduleTime">
                                    Payment will be collected automatically on {{invoice.autopayScheduledTime | tzDate(invoice.organization.invoiceTimeZone)}}
                                </a>
                                <a href="#" @click="showAutopayModal = true" v-else>None</a>
                            </p>
                        </div>
                    </div>
                </div>
                <div class="grid-item grid-item-half">
                    <table class="table table-compact">
                        <tbody>
                        <tr>
                            <td class="text-muted">Subtotal</td>
                            <td class="text-right is-numeric">{{invoice.subtotalAmount | coverEmpty | monetize(invoice.currency)}}</td>
                        </tr>
                        <tr>
                            <td class="text-muted">Discounts</td>
                            <td class="text-right is-numeric">{{invoice.discountAmount | coverEmpty | monetize(invoice.currency)}}</td>
                        </tr>
                        <tr v-if="invoice.hasShipping">
                            <td class="text-muted">Shipping</td>
                            <td class="text-right is-numeric">{{invoice.shippingAmount | monetize(invoice.currency)}}</td>
                        </tr>
                        <tr>
                            <td class="text-muted">Taxes</td>
                            <td class="text-right is-numeric">{{invoice.taxAmount | coverEmpty | monetize(invoice.currency)}}</td>
                        </tr>
                        <tr>
                            <td  class="text-muted"><strong>Total</strong></td>
                            <td class="text-right is-numeric">{{invoice.amount | monetize(invoice.currency)}}</td>
                        </tr>
                        <slot name="transactionItems"/>
                        <slot name="creditMemoItems"/>
                        <slot name="amountDue"/>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        <autopay-modal
            v-if="showAutopayModal"
            @update="updateAutopayAndRetry"
            @close="showAutopayModal = false"
            :original-invoice="invoice"
        />
    </div>
</template>

<script>
    import {vInlineEdit} from '@/components/form';

    export default {
        components: {
            vInlineEdit,
            AutopayModal: () => import(/* webpackChunkName: "invoices" */ '@/modules/invoices/components/autopay-modal'),
        },
        props: {
            invoice: {
                type: Object,
                required: true,
            },
        },
        computed: {
            hasAutopayScheduleTime() {
                return this.invoice.autopayScheduledTime !== null;
            },
            getInvoiceBreakdown() {
                const formatAmount = amount => this.$filters.monetize(amount, this.invoice.currency);
                const formatAmountNotEmpty = amount => formatAmount(this.$filters.coverEmpty(amount));
                const shipping = formatAmount(this.invoice.shippingAmount);
                const autopayScheduledTime = this.$filters.dateUTC(this.invoice.autopayScheduledTime, 'date');
                const breakdown = {
                    subtotal: formatAmountNotEmpty(this.invoice.subtotalAmount),
                    discounts: formatAmountNotEmpty(this.invoice.discountAmount),
                    shipping: null,
                    taxes: formatAmountNotEmpty(this.invoice.taxAmount),
                    total: formatAmount(this.invoice.amount),
                    autopay: null,
                };
                if (this.invoice.hasShipping) {
                    breakdown.shipping = shipping;
                }
                if (this.hasAutopayScheduleTime) {
                    breakdown.autopay = `Payment will be collected automatically on ${autopayScheduledTime}`;
                }
                return breakdown;
            },
        },
        data() {
            return {
                showAutopayModal: false,
            };
        },
        methods: {
            updateAutopayAndRetry(invoice) {
                this.$emit('update-autopay-and-retry', invoice);
            },
            editInvoice(field, value) {
                this.$emit('update-invoice-field', {field, value});
            },
        },
    };
</script>
